import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchJapaMantras, incrementJapa, getMyData, saveMyData, premiumStatus } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import JourneyStrip from "@/components/JourneyStrip";
import ShareButton from "@/components/ShareButton";
import InviteButton from "@/components/InviteButton";
import { HERO_ART } from "@/lib/heroes";
import { RotateCcw, Users, Flame, Check } from "lucide-react";
import JapaMala from "@/components/JapaMala";
import JapaAudio from "@/components/JapaAudio";
import { toast } from "sonner";
import { fireGamify } from "@/lib/gamify";

const LS = "dharmartha_japa";
const IMPORTS_LS = "dharmartha_japa_imports";
const MALA = 108;
const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => new Date(Date.now() - 864e5).toISOString().slice(0, 10);

const loadLocal = () => { try { return JSON.parse(localStorage.getItem(LS)) || {}; } catch { return {}; } };
const blank = () => ({ total: 0, today: 0, todayDate: todayStr(), streak: 0, lastDate: null });

export default function Japa() {
  const { pick } = useLang();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [mantras, setMantras] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [japa, setJapa] = useState(loadLocal);
  const [community, setCommunity] = useState({});
  const pendingRef = useRef({});
  const flushTimer = useRef(null);
  const saveTimer = useRef(null);
  const gamifyPending = useRef(0);
  const gamifyTimer = useRef(null);
  const adhocRef = useRef(new Set());
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => { premiumStatus().then((s) => setIsPremium(!!s?.active)).catch(() => setIsPremium(false)); }, [user]);

  useEffect(() => { adhocRef.current = new Set(mantras.filter((m) => m.adhoc).map((m) => m.id)); }, [mantras]);

  // Any mantra from the library can open straight into the counter via
  // /japa?m=<id>&deva=&translit=&meaning= — it's added as an ad-hoc mantra.
  // Item 9: free members may save ONE imported mantra; premium is unlimited.
  useEffect(() => {
    const m = params.get("m");
    if (!m) return;
    const deva = params.get("deva") || m;
    const translit = params.get("translit") || "";
    const meaning = params.get("meaning") || "";
    const label = params.get("label") || "";
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(IMPORTS_LS) || "[]"); } catch { saved = []; }
    if (!isPremium && !saved.includes(m) && saved.length >= 1) {
      toast(pick("प्रीमियम आवश्यक है", "Premium needed"), {
        id: "japa-import-limit",
        description: pick(
          "निःशुल्क सदस्य 1 आयातित मंत्र सहेज सकते हैं। किसी भी मंत्र का जप करने के लिए प्रीमियम लें।",
          "Free members can save 1 imported mantra. Upgrade to Premium to chant any mantra from the library.",
        ),
        action: { label: pick("प्रीमियम", "Premium"), onClick: () => { window.location.assign("/panchanga-premium"); } },
      });
      return;
    }
    if (!saved.includes(m)) { saved.push(m); try { localStorage.setItem(IMPORTS_LS, JSON.stringify(saved)); } catch { /* ignore */ } }
    setMantras((list) => (list.find((x) => x.id === m) ? list : [{ id: m, deva, translit, meaning, label: (label || deva.split("\n")[0] || m), community_count: 0, adhoc: true }, ...list]));
    setActiveId(m);
  }, [params, isPremium, pick]);

  useEffect(() => {
    fetchJapaMantras().then((m) => {
      setMantras((prev) => {
        const adhoc = prev.filter((x) => x.adhoc && !m.find((b) => b.id === x.id));
        return [...adhoc, ...m];
      });
      setCommunity(Object.fromEntries(m.map((x) => [x.id, x.community_count])));
      setActiveId((a) => a || m[0]?.id);
    }).catch(() => {});
  }, []);

  // Sync personal japa from account on login (merge by max totals).
  useEffect(() => {
    if (!user) return;
    getMyData().then((d) => {
      const server = d.japa || {};
      setJapa((local) => {
        const merged = { ...server };
        Object.entries(local).forEach(([k, v]) => {
          const s = server[k];
          merged[k] = !s || (v.total || 0) >= (s.total || 0) ? v : s;
        });
        localStorage.setItem(LS, JSON.stringify(merged));
        return merged;
      });
    }).catch(() => {});
  }, [user]);

  const persist = useCallback((next) => {
    setJapa(next);
    localStorage.setItem(LS, JSON.stringify(next));
    if (user) {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => { saveMyData({ japa: next }).catch(() => {}); }, 1200);
    }
  }, [user]);

  const flushCommunity = useCallback(() => {
    const pend = pendingRef.current;
    Object.keys(pend).forEach((id) => {
      const n = pend[id];
      if (n > 0) { pend[id] = 0; incrementJapa(id, n).then((r) => setCommunity((c) => ({ ...c, [id]: r.community_count }))).catch(() => {}); }
    });
  }, []);

  // Send each chanted bead to the gamification ledger (batched/debounced) so
  // sadhana points stay perfectly in sync with what you actually chant. Points
  // are awarded server-side per completed mala; local resets never remove them.
  const flushGamify = useCallback(() => {
    const n = gamifyPending.current;
    if (n > 0) { gamifyPending.current = 0; fireGamify("japa", null, n); }
  }, []);

  useEffect(() => () => { clearTimeout(flushTimer.current); clearTimeout(gamifyTimer.current); flushCommunity(); flushGamify(); }, [flushCommunity, flushGamify]);

  const count = useCallback(() => {
    if (!activeId) return;
    const isAdhoc = adhocRef.current.has(activeId);
    if (!isAdhoc) {
      setCommunity((c) => ({ ...c, [activeId]: (c[activeId] || 0) + 1 }));
      pendingRef.current[activeId] = (pendingRef.current[activeId] || 0) + 1;
      clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(flushCommunity, 2500);
    }

    // Queue this bead for the gamification ledger (batched).
    gamifyPending.current += 1;
    clearTimeout(gamifyTimer.current);
    gamifyTimer.current = setTimeout(flushGamify, 2500);

    setJapa((prev) => {
      const cur = { ...(prev[activeId] || blank()) };
      const t = todayStr();
      if (cur.todayDate !== t) {
        cur.streak = cur.lastDate === yesterdayStr() ? (cur.streak || 0) + 1 : 1;
        cur.today = 0; cur.todayDate = t; cur.lastDate = t;
      } else if (!cur.lastDate) { cur.lastDate = t; cur.streak = cur.streak || 1; }
      cur.total = (cur.total || 0) + 1;
      cur.today = (cur.today || 0) + 1;
      const next = { ...prev, [activeId]: cur };
      localStorage.setItem(LS, JSON.stringify(next));
      if (user) { clearTimeout(saveTimer.current); saveTimer.current = setTimeout(() => saveMyData({ japa: next }).catch(() => {}), 1500); }
      if (cur.total % MALA === 0) { toast(pick("एक माला पूर्ण 🙏", "One mala complete 🙏"), { description: `${cur.total / MALA} × 108` }); }
      return next;
    });
  }, [activeId, flushCommunity, flushGamify, user, pick]);

  // Space / Enter to count.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.code === "Space" || e.code === "Enter") && !["INPUT", "TEXTAREA", "BUTTON", "A"].includes(e.target.tagName)) {
        e.preventDefault(); count();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count]);

  const resetMala = () => {
    const cur = { ...(japa[activeId] || blank()) };
    cur.total = Math.floor(cur.total / MALA) * MALA;
    persist({ ...japa, [activeId]: cur });
    toast(pick("वर्तमान माला रीसेट", "Current mala reset"), { description: pick("अर्जित अंक सुरक्षित हैं", "Your earned points are safe") });
  };

  const resetToday = () => {
    const cur = { ...(japa[activeId] || blank()) };
    cur.today = 0;
    persist({ ...japa, [activeId]: cur });
    toast(pick("आज की संख्या रीसेट", "Today's count reset"), { description: pick("अर्जित अंक सुरक्षित हैं", "Your earned points are safe") });
  };

  const active = mantras.find((m) => m.id === activeId);
  const stat = japa[activeId] || blank();
  const bead = stat.total % MALA;
  const malas = Math.floor(stat.total / MALA);
  const filled = stat.total === 0 ? 0 : ((stat.total - 1) % MALA) + 1;
  const communityTotal = Object.values(community).reduce((a, b) => a + (b || 0), 0);

  return (
    <>
      <Helmet><title>Japa · Chant Together · Dharmartha</title>
        <meta name="description" content="A japa counter to chant your favourite mantra, track your mala, daily count and streak, and add to the live community tally." /></Helmet>

      <PageHero
        art={HERO_ART.devotion}
        overline={pick("जप · सामूहिक साधना", "Japa · Chant together")}
        deva="जप"
        en={pick("मंत्र जप — साथ मिलकर", "Chant your mantra — together")}
        tagline={pick("एक मंत्र चुनें, स्पर्श करके जप करें, अपनी माला एवं नित्य संख्या रखें, और सामूहिक जप में सहभागी बनें।", "Pick a mantra, tap to chant, keep your mala and daily count, and add to the live community tally.")}
      >
        <div className="mt-6 inline-flex items-center gap-2 border border-line bg-surface/70 px-4 py-2.5 text-sm text-ink-2" data-testid="japa-community-total">
          <Users className="h-4 w-4 text-saffron" />
          {pick("सामूहिक जप", "Community japa")}: <span className="font-serif-latin text-lg text-ink">{communityTotal.toLocaleString()}</span>
        </div>
      </PageHero>

      <section className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-16">
        {/* mantra picker — single horizontal-scroll row keeps it tidy */}
        <div className="mb-10 -mx-1 flex gap-2 overflow-x-auto no-scrollbar px-1 pb-2" data-testid="japa-mantra-picker">
          {mantras.map((m) => (
            <button key={m.id} data-testid={`japa-mantra-${m.id}`} onClick={() => setActiveId(m.id)}
              className={`shrink-0 max-w-[14rem] truncate rounded-full border px-4 py-2 font-deva text-sm transition-colors ${activeId === m.id ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2 hover:border-[var(--ink)]"}`}>
              {m.label || m.deva}
            </button>
          ))}
        </div>

        {active && (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            {/* Counter */}
            <div className="flex min-w-0 flex-col items-center text-center">
              <p className="whitespace-pre-line font-deva text-3xl text-ink deva-lh md:text-4xl">{active.deva}</p>
              {active.translit && <p className="mt-2 whitespace-pre-line text-base italic text-ink-2">{active.translit}</p>}
              {active.meaning && <p className="mt-1 text-sm text-ink-3">{active.meaning}</p>}

              <JapaMala filled={filled} malaNumber={malas} total={stat.total} onCount={count} hint={pick("माला पर स्पर्श करें या स्पेस दबाएँ", "Tap the mala or press Space")} />

              <JapaAudio />

              <div className="mt-6 flex items-center gap-6">
                <div className="text-center"><p className="font-serif-latin text-2xl text-ink" data-testid="japa-malas">{malas}</p><p className="text-xs text-ink-3">{pick("मालाएँ", "malas")}</p></div>
                <div className="text-center"><p className="font-serif-latin text-2xl text-ink">{stat.total}</p><p className="text-xs text-ink-3">{pick("कुल", "total")}</p></div>
                <div className="text-center"><p className="font-serif-latin text-2xl text-ink">{stat.today}</p><p className="text-xs text-ink-3">{pick("आज", "today")}</p></div>
                <div className="flex flex-col items-center text-saffron"><p className="inline-flex items-center gap-1 font-serif-latin text-2xl" data-testid="japa-streak"><Flame className="h-4 w-4" />{stat.streak || 0}</p><p className="text-xs text-ink-3">{pick("दिन", "day streak")}</p></div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button onClick={resetMala} data-testid="japa-reset" className="inline-flex items-center gap-1.5 text-sm text-ink-3 hover:text-saffron"><RotateCcw className="h-3.5 w-3.5" /> {pick("वर्तमान माला रीसेट", "Reset current mala")}</button>
                <span className="text-ink-3">·</span>
                <button onClick={resetToday} data-testid="japa-reset-today" className="inline-flex items-center gap-1.5 text-sm text-ink-3 hover:text-saffron"><RotateCcw className="h-3.5 w-3.5" /> {pick("आज की संख्या रीसेट", "Reset today")}</button>
                <ShareButton variant="button" testid="japa-share" title="Japa on Dharmartha" text={pick(`मैं ${active.deva} का जप कर रहा/रही हूँ`, `I'm chanting ${active.translit} on Dharmartha`)} label={pick("साझा करें", "Share")} />
                <InviteButton context={pick("जप", "Japa")} testid="japa-invite" />
              </div>
              {user && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-3" data-testid="japa-points-note">
                  <Check className="h-3.5 w-3.5 text-saffron" /> {pick("अर्जित साधना अंक रीसेट से प्रभावित नहीं होते।", "Resets never affect your earned sadhana points.")}
                </p>
              )}
            </div>

            {/* Community */}
            <div className="min-w-0">
              <h2 className="mb-4 flex items-center gap-2 overline-label text-ink-3"><Users className="h-4 w-4" /> {pick("सामूहिक जप संख्या", "Live community count")}</h2>
              <div className="space-y-2">
                {mantras.filter((m) => !m.adhoc).map((m) => (
                  <div key={m.id} className={`flex items-center justify-between border p-3 ${m.id === activeId ? "border-saffron/40 bg-[rgba(199,80,0,0.04)]" : "border-line bg-surface"}`}>
                    <div className="min-w-0">
                      <p className="truncate font-deva text-ink">{m.deva}</p>
                      <p className="text-xs text-ink-3">{m.translit}</p>
                    </div>
                    <p className="ml-3 shrink-0 font-serif-latin text-lg text-ink">{(community[m.id] || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              {!user && (
                <p className="mt-5 border border-line bg-[var(--surface-alt)]/40 p-4 text-sm text-ink-2">
                  <Check className="mb-1 mr-1 inline h-4 w-4 text-saffron" />
                  {pick("अपना जप सभी उपकरणों पर सहेजने हेतु ", "To save your japa across all your devices, ")}
                  <a href="/register" className="text-saffron underline">{pick("निःशुल्क खाता बनाएँ", "create a free account")}</a>.
                </p>
              )}
            </div>
          </div>
        )}
        <JourneyStrip links={["groups", "dainik", "mantras"]} />
      </section>
    </>
  );
}
